import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { bool, node, string } from 'prop-types';
import { Button } from '@thd-olt-component-react/button';
import { Heading } from '@thd-olt-component-react/heading';
import { string as stringType, params, shape, number as numberType, customType, bool as boolType } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { CustomerImageCarousel } from './CustomerImageCarousel/CustomerImageCarousel.component';
import { Col, Row } from '@thd-olt-component-react/grid';
import { PresentationProvider } from '../context/PresentationProvider'
import { track } from '../ratings-reviews-analytics';
import { Filter } from './Filter/Filter.component';
import { FirstReview } from './FirstReview/FirstReview.component';
import { RatingsAndReviewsMobile } from './Mobile/RatingsAndReviewsMobile';
import { Review } from './Review/Review.component';
import ReviewHelper from './Review/helpers/ReviewHelper';
import { ReviewPager } from './Paging/ReviewPager.component';
import { ReviewSentiments } from './Filter/components/ReviewSentiments/ReviewSentiments.component';
import { WriteAReview } from './WriteAReview/WriteAReview.component';
import { Overview } from './Overview/Overview';
import { Search, Sort } from './Filter/components';
import './ratings-and-reviews.style.scss';
import { useProductReviews } from './useProductReviews';
import { dataModel } from './ReviewDataModel';
import classNames from 'classnames';


// TODO: Move to a constants file or similar
const DEFAULT_FILTERS =  [
  {
    checked: false,
    id: 'rating_5',
    value: '5',
    count: 0
  },
  {
    checked: false,
    id: 'rating_4',
    value: '4',
    count: 0
  },
  {
    checked: false,
    id: 'rating_3',
    value: '3',
    count: 0
  },
  {
    checked: false,
    id: 'rating_2',
    value: '2',
    count: 0
  },
  {
    checked: false,
    id: 'rating_1',
    value: '1',
    count: 0
  },
  {
    checked: false,
    id: 'verified',
    value: 'verified',
    count: 0
  }
];
const DEFAULT_PAGE_CONTEXT = {
  totalPages: 3,
  currentPage: 1,
  pageNeighbours: 1
};
const DEFAULT_SORTBY = 'photoreview';
const initialState = {
  filters: [...DEFAULT_FILTERS],
  firstPageReviews: {},
  pageContext: DEFAULT_PAGE_CONTEXT,
  productStatistics: {},
  searchText: null,
  selectedSentiment: null,
  sortBy: DEFAULT_SORTBY
};

const stateReducer = (state, action) => {
  const { type, value } = action;
  const { filters, selectedSentiment, pageContext, searchText } = state;
  switch (type) {
    case 'filter':
      pageContext.currentPage = 1;
      const updatedFilters = filters.map((filter) => {
        return {
          checked: filter.value === value.toString() ? !filter.checked : filter.checked,
          id: filter.id,
          value: filter.value,
          count: filter.count
        };
      });
      const isChecked = updatedFilters.find((filter) => filter.value === value.toString())?.checked;
      if (isChecked) {
        track('event', {
          origin: 'ratings and reviews',
          value: value,
          location: type
        });
      }
      return {
        ...state,
        pageContext,
        filters: updatedFilters,
        selectedSentiment: null,
        searchText: searchText
      };
    case 'removeFilter':
      pageContext.currentPage = 1;
      const updatedSentiment = value.Feature ? null : selectedSentiment;
      let removeUpdatedFilters = filters;
      if (!value.Feature) {
        removeUpdatedFilters = filters.map((filter) => {
          return {
            checked: filter.value === value.value ? !filter.checked : filter.checked,
            id: filter.id,
            value: filter.value,
            count: filter.count
          };
        });
      }
      return {
        ...state,
        pageContext,
        filters: removeUpdatedFilters,
        selectedSentiment: updatedSentiment
      };
    case 'pageChange':
      return {
        ...state,
        pageContext: value
      };
    case 'search':
      pageContext.currentPage = 1;
      return {
        ...state,
        pageContext,
        searchText: value
      };
    case 'sentiment':
      // on sentiment change reset other filters, if the sentiment is currently selected reset that as well
      const { Feature } = value;
      pageContext.currentPage = 1;
      return {
        ...state,
        filters: [...DEFAULT_FILTERS],
        pageContext,
        searchText: null,
        selectedSentiment: Feature === selectedSentiment?.Feature ? null : value,
        sortBy: DEFAULT_SORTBY
      };
    case 'sort':
      pageContext.currentPage = 1;
      return {
        ...state,
        pageContext,
        sortBy: value
      };
    case 'totalPages':
      pageContext.totalPages = value;
      return {
        ...state,
        pageContext
      };
    default:
      console.error('Unhandled Action: ', action);
  }
};

export const RatingsAndReviews = (props) => {
  const {
    apiKey = '', children, itemId, prosAndCons, target, forceCondensedLayout
  } = props;
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const persistedStatistics = useRef({});
  const initialReviews = useRef({});
  const persistedPageContext = useRef();
  const carouselRef = useRef();
  const { averageRating, FilterSelected, loading, reviewModels = [], statistics, totalReviews } = useProductReviews({ itemId, ...state });
  useEffect(() => {
    if (!persistedPageContext.current) {
      persistedPageContext.current = state.pageContext;
      return;
    }
    if (persistedPageContext.current.currentPage !== state.pageContext.currentPage) {
      persistedPageContext.current = state.pageContext;
      if (carouselRef.current) {
        carouselRef.current.scrollIntoView({
          behavior: 'auto' // do not use smooth. It'll cause the page to scroll to the bottom while it updates the results.
        });
      }
    }
  }, [state.pageContext]);

  const { channel } = useContext(ExperienceContext);
  const useCondensedLayout = forceCondensedLayout || channel === 'mobile';
  const { filters, pageContext, searchText, selectedSentiment, sortBy } = state;
  const hasProductData = averageRating || totalReviews;
  const sentimentChange = (sentiment) => dispatch({type: 'sentiment', value: sentiment});
  if (loading && !persistedStatistics.current[itemId]) {
    return null;
  }
  const selected = selectedSentiment ? [selectedSentiment] : filters.filter((rf) => rf.checked);
  let productReviews = reviewModels.length || searchText || selected.length ? reviewModels : initialReviews.current[itemId] || [];
  const noFallbacks = !(hasProductData || productReviews || persistedStatistics.current[itemId]);
  if (noFallbacks) {
    return null;
  }
  const updatedTotalpage = selectedSentiment ? Math.ceil(selectedSentiment?.TotalResults/10) || 1 : statistics?.totalPages || 1;
  const onFilterChange = (updatedFilters, scrollToFilter = true) => {
    dispatch({type: 'filter', value: updatedFilters});
    const searchAndSort = document.getElementById('search-and-sort');
    if (useCondensedLayout) {
      const overlay = document.querySelector('.content-card__overlay');
      if (overlay && scroll) {
        overlay.scroll({behavior: 'smooth', top: searchAndSort.getBoundingClientRect().top})
      }
    } else {
      const stickyNav = document.getElementById('sticky-nav');
      const currentPosition = window.scrollY || window.pageYOffset
      const scrollToPosition = searchAndSort.getBoundingClientRect().top - stickyNav.offsetHeight  + currentPosition;
      window.scroll({behavior: 'smooth', top: scrollToPosition});
    }
  };
  if(state.pageContext.totalPages !== updatedTotalpage) {
    dispatch({type: 'totalPages', value: updatedTotalpage});
  }
  const { SearchText } = FilterSelected || {};
  if (persistedStatistics.current && !persistedStatistics.current[itemId]) {
    persistedStatistics.current[itemId] = statistics;
    initialReviews.current[itemId] = reviewModels;
  }
  const reviewStatistics = persistedStatistics.current && persistedStatistics.current[itemId] ? persistedStatistics.current[itemId] : statistics;
  const noResults = state.searchText && reviewModels.length === 0 && !loading;

  const pagerDisplayText = ({ topPager = true } = {}) => {
    const pageSummaryReviewText = classNames(
      'pager-summary-review-text', {
        'pager-summary-review-text-bottom': !topPager
      }
    )
    const totalReviews = statistics?.totalReviewCount;
    const reviewsDisplayPerPage = 10;
    if (!totalReviews) {
      return topPager && !loading ? <span className="pager-no-results">No results found</span> : null;
    }
    const start = (pageContext.currentPage - 1) * reviewsDisplayPerPage + 1;
    const end = ((start + 9) > totalReviews) ? totalReviews : start + 9;
    const totalWithCommas = ReviewHelper.numberWithCommas(totalReviews);
    return (
      <div className="pager-summary">
        {`Showing `}
        <span className="pager-summary__bold">{start}</span>
        -
        <span className="pager-summary__bold">{end}</span>
         {` of `}
        <span className="pager-summary__bold">{totalWithCommas}</span>
        <span className={pageSummaryReviewText}>
          {!useCondensedLayout || !topPager ? ` reviews` : ''}
        </span>
      </div>
    )
  }
  const renderRatingsAndReviews = () => {
    if (useCondensedLayout) {
      // mobile renders first 3 reviews
      const firstThreeReviews = reviewModels.slice(0, 3).filter(review => !!review);
      return (
        <div className="ratings-reviews" id="ratings-and-reviews">
          <RatingsAndReviewsMobile
            apiKey={apiKey}
            filterCheckboxes={filters}
            itemId={itemId}
            onDeselectFilter={(checkbox) => dispatch({ type: 'removeFilter', value: checkbox})}
            onFilterChange={onFilterChange}
            mobileReviewsOnFirstPage={firstThreeReviews}
            noResults={noResults}
            onSearch={(searchTerm) => dispatch({type: 'search', value: searchTerm})}
            onPageChange={(updatedPageContext) => dispatch({
              type: 'pageChange',
              value: updatedPageContext
            })}
            onSentimentChange={sentimentChange}
            onSearchClear={() => dispatch({ type: 'search', value: null})}
            onSortChange={(updatedSortValue) => dispatch({type: 'sort', value: updatedSortValue})}
            prosAndCons={prosAndCons}
            searchTerm={SearchText}
            selected={selected}
            sortBy={sortBy}
            reviewModels={reviewModels}
            reviewStatistics={reviewStatistics}
            selectedSentiment={selectedSentiment}
            target={target}
            pagerDisplayText={pagerDisplayText}
          >
            {children}
          </RatingsAndReviewsMobile>
        </div>
      );
    }

    if (hasProductData && !(searchText || productReviews.length || selected.length)) {
     return (
       <div className="ratings-reviews" id="ratings-and-reviews">
         <Heading title="Customer Reviews" underline className="overview-heading"/>
         <Overview apiKey={apiKey} reviewStatistics={{}} itemId={itemId} />
         <WriteAReview apiKey={apiKey} itemId={itemId} totalReviews={totalReviews} />
       </div>
     )
    }

    if (!(productReviews.length || persistedStatistics.current[itemId]?.length || searchText || selected.length)) {
      return (<FirstReview
        apiKey={apiKey}
        itemId={itemId}
        reviewStatistics={reviewStatistics}
      />);
    }

    return (
      <div className="ratings-reviews" id="ratings-and-reviews">
        <Heading title="Customer Reviews" underline className="overview-heading"/>
        <Overview
          apiKey={apiKey}
          clickableFilters
          itemId={itemId}
          reviewStatistics={reviewStatistics}
          filterCheckboxes={filters}
          onFilterChange={onFilterChange}
          selectedSentiment={selectedSentiment}
        />
        <div className="customer-image-carousel__wrapper" ref={carouselRef}>
          <CustomerImageCarousel itemId={itemId}/>
        </div>
        {prosAndCons && (
          <ReviewSentiments
          itemId={itemId}
          selectedSentiment={selectedSentiment}
          onSentimentChange={sentimentChange}
          />
        )}

        <>
          <Row className="ratings-reviews__search-and-sort" id="search-and-sort">
            <Col xs={3} sm={3} md={3} lg={3} fallback={3}
                 className="ratings-and-reviews__search-and-filter">
              <Search
                onSearch={(searchTerm) => dispatch({ type: 'search', value: searchTerm})}
                searchTerm={state.searchText}
                noResults={noResults}
              />
            </Col>
            <Col xs={9} sm={9} md={9} lg={9} fallback={9}
                 className="ratings-and-reviews__search-and-filter">
              <Filter
                onChange={(updatedFilters) => dispatch({ type: 'filter', value: updatedFilters})}
                ratingFilters={filters}
                reviewStatistics={reviewStatistics}
                selectedSentiment={selectedSentiment}
              />
            </Col>
          </Row>
          <Row className="ratings-reviews__sort-and-filter">
            <Col sm={3} md={3} lg={3} fallback={3}>
              {pagerDisplayText({ topPager: true })}
            </Col>
            <Col xs={6} sm={7} md={7} lg={7} fallback={7} className="ratings-reviews__pills-wrapper">
              {selected.length > 0
              && (
                <>
                  {selected
                    .map((checkbox, checkboxIndex) => {
                      const pillText =  checkbox.value === 'verified' ? 'Verified Purchases' : `${checkbox.value} Star`;
                      return  (
                        <Button
                          key={checkboxIndex}
                          id={checkbox.id}
                          onClick={() => dispatch({ type: 'removeFilter', value: checkbox})}
                          filter
                        >
                          {selectedSentiment ? selectedSentiment.Feature : pillText}
                        </Button>
                      )
                    }
                    )}
                </>
              )}
              {
                SearchText
                && (
                  <Button
                    key="search"
                    id="search"
                    onClick={() => dispatch({ type: 'search', value: null})}
                    filter
                  >
                    {noResults && SearchText ? 'Clear Search Term' : SearchText}
                  </Button>
                )
              }
            </Col>
            <Col xs={3} sm={2} md={2} lg={2} fallback={2}>
              <Sort onChange={(newSort) => dispatch({ type: 'sort', value: newSort})} sortBy={sortBy} />
            </Col>
          </Row>
        </>


        {(productReviews).map((review, reviewIndex) => {
          return (
            <Review
              review={review}
              id={reviewIndex}
              key={`review-${reviewIndex}`}
              searchTerm={SearchText}
              apiKey={apiKey}
            />
          );
        })}
        <Row>
          <Col xs="12" sm="12" md="12" className="pager-section">
              <div className="ratings-reviews__review-pager">
                <ReviewPager
                  itemId={itemId}
                  reviewsPageContext={pageContext}
                  onPageChanged={(newPageContext) => dispatch({ type: 'pageChange', value: newPageContext })}
                />
              </div>
            <Row className="ratings-reviews__review-pager__summary">
              { pagerDisplayText({ topPager: false }) }
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <PresentationProvider useCondensedLayout={useCondensedLayout}>
      {renderRatingsAndReviews()}
    </PresentationProvider>
  )
};

RatingsAndReviews.displayName = 'RatingsAndReviews';

RatingsAndReviews.propTypes = {
  /*
    This is a bad practice.
    APIs should not be called directly.
    There is a story to refactor this to use a graphql mutation:
    https://www.pivotaltracker.com/story/show/173363061
  */
  apiKey: string,
  children: node,
  collectionId: string,
  itemId: string.isRequired,
  prosAndCons: bool,
  forceCondensedLayout: bool,
  target: string
};

RatingsAndReviews.defaultProps = {
  apiKey: 'x5w9jA8tWVGcqRhujrHTvjRwQfH8MkFc',
  children: null,
  collectionId: null,
  prosAndCons: false,
  forceCondensedLayout: false,
  target: ''
};

RatingsAndReviews.dataModel = dataModel;
